import { useGetSelectedMeasuredAsCriteriaOption } from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { getQuantifyStateFromEQLProperties } from '@modules/quantify/utils';
import { EqlQuery, QuantifyMetadata } from 'generated/graphql';
import { useEffect, useRef } from 'react';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { IsLoading } from 'v2/core/repository/edge-server/types';

interface UseInitializeQuantifyStoreFromEqlTokenProps {
  isReadOnly?: boolean;
  eqlQuery: EqlQuery | undefined;
  quantifyMetadata?: QuantifyMetadata;
}

export function useInitializeQuantifyStoreFromEqlToken({
  isReadOnly,
  eqlQuery: eql,
  quantifyMetadata,
}: UseInitializeQuantifyStoreFromEqlTokenProps) {
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const quantifyStateGroupByField = useQuantifyStore(
    ({ query }) => query.groupByField,
  );
  const quantifyStateFormulaQuery = useQuantifyStore(
    ({ query }) => query.formulaQuery,
  );
  const { data: sourceDetailsMap, status } = useGetSourceDetailsMap();
  const areSourceDetailsLoading = IsLoading(status);

  const dependencies = {
    eql,
    quantifyStateGroupByField,
    sourceDetailsMap,
    isReadOnly,
    quantifyStateFormulaQuery,
    quantifyMetadata,
  };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  useEffect(() => {
    if (areSourceDetailsLoading) {
      return;
    }

    const eqlQuery = dependenciesRef.current.eql;

    if (eqlQuery?.properties) {
      const { query, visual } = getQuantifyStateFromEQLProperties({
        properties: eqlQuery?.properties,
        sourceDetailsMap: dependenciesRef.current.sourceDetailsMap,
        quantifyMetadata: dependenciesRef.current.quantifyMetadata,
        legacyQuantifyMetadata: undefined,
        createdAt: Date.now(),
      });

      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: { ...query },
      });

      dispatch({
        type: QuantifyActionType.SET_VISUAL,
        payload: {
          ...visual,
          isEditMode: !dependenciesRef.current.isReadOnly,
          shouldSkipPercentageFormula: true,
        },
      });
    }
  }, [dispatch, areSourceDetailsLoading]);

  const { selectedMeasuredAsCriteriaOption, isLoading: isCriteriaLoading } =
    useGetSelectedMeasuredAsCriteriaOption({
      formulaID: quantifyStateFormulaQuery?.eqlFunction,
      groupBy: quantifyStateGroupByField,
      measuredAsCriteria: undefined,
      shouldSkip:
        !quantifyStateFormulaQuery?.eqlFunction ||
        !!eql?.properties?.formulaQuery?.criteria,
    });

  useEffect(() => {
    if (selectedMeasuredAsCriteriaOption) {
      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: {
          formulaQuery: {
            ...dependenciesRef.current.quantifyStateFormulaQuery,
            criteria: selectedMeasuredAsCriteriaOption,
          },
        },
      });
    }
  }, [dispatch, selectedMeasuredAsCriteriaOption]);

  return { isLoading: areSourceDetailsLoading || isCriteriaLoading };
}
